import type { TFunction } from 'i18next';

import { ApiError } from './api';

export type ErrorCategory =
  | 'validation'
  | 'session'
  | 'forbidden'
  | 'notFound'
  | 'conflict'
  | 'rateLimit'
  | 'server'
  | 'network'
  | 'unexpected';

export interface DescribedError {
  /** What failed, in the user's words. */
  title: string;
  /** What happens next / what they can do. */
  hint: string;
  /** Short, copyable reference code — the same request id the backend logged. */
  ref: string | null;
  category: ErrorCategory;
  /** Raw details, only ever built in development. */
  devDetails?: string;
}

function categoryOf(err: unknown): ErrorCategory {
  if (!(err instanceof ApiError)) return 'unexpected';
  if (err.status === 0 || err.code === 'ERR_NETWORK') return 'network';
  if (err.status === 401 || err.code === 'SESSION_EXPIRED') return 'session';
  if (err.status === 403) return 'forbidden';
  if (err.status === 404) return 'notFound';
  if (err.status === 409) return 'conflict';
  if (err.status === 429) return 'rateLimit';
  if (err.status >= 500) return 'server';
  return 'validation';
}

/** `3f9c2a71-…` → `3F9C-2A71`: short enough to read out over the phone. */
export function shortRef(requestId: string | null | undefined): string | null {
  if (!requestId) return null;
  const compact = requestId.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 8);
  return compact.length === 8 ? `${compact.slice(0, 4)}-${compact.slice(4)}` : compact || null;
}

function translatedCode(code: string, t: TFunction): string | null {
  // `errors.codes.X` is the catalog; the older `errors.x_y` keys still cover
  // a few collection/stock codes.
  for (const key of [`errors.codes.${code}`, `errors.${code.toLowerCase()}`]) {
    const value = t(key, { defaultValue: '' });
    if (value) return value;
  }
  return null;
}

/**
 * The only place that decides what an error looks like on screen
 * (DESIGN.md §8). Backend `message`s are English, technical and never shown:
 * the stable `code` is translated, otherwise the HTTP category speaks.
 */
export function describeError(err: unknown, t: TFunction): DescribedError {
  if (typeof err === 'string') {
    return { title: err, hint: '', ref: null, category: 'unexpected' };
  }
  const category = categoryOf(err);
  const api = err instanceof ApiError ? err : null;
  const fromCode = api ? translatedCode(api.code, t) : null;

  // A translated code can carry its own next step; the category hint
  // ("reload the page" for every 409) is wrong for most business conflicts.
  const codeHint = api && fromCode ? t(`errors.codeHints.${api.code}`, { defaultValue: '' }) : '';
  let hint = codeHint || t(`errors.category.${category}.hint`);
  if (category === 'rateLimit' && api?.retryAfterSeconds) {
    hint = t('errors.category.rateLimit.hintSeconds', { count: api.retryAfterSeconds });
  }

  const described: DescribedError = {
    title: fromCode ?? t(`errors.category.${category}.title`),
    hint,
    ref: shortRef(api?.requestId),
    category,
  };

  if (import.meta.env.DEV) {
    described.devDetails = api
      ? `${api.code} · HTTP ${api.status} · ${api.message} · ${api.requestId ?? ''}`
      : err instanceof Error
        ? `${err.name}: ${err.message}`
        : String(err);
  }
  return described;
}
